import { MapPin, Calendar, ArrowUpRight } from "lucide-react";
import { HERO_IMAGES } from "@/data/products";
import { goToEnquiryAs } from "@/components/EnquiryForm";

// A slim, high-impact "come see it in person" banner for use near the top of Bushtech product pages.
// `enquiryProduct` (optional): when set, "Book A Viewing" pre-fills the footer
// enquiry form's Product Interest dropdown via goToEnquiryAs() instead of a
// plain #enquire anchor jump — needed on pages with no local #enquire section
// of their own (e.g. category listing pages), where a bare anchor jump lands
// on the generic footer form with no product context.
export const DemoBanner = ({ product = "Bushtech", image, showGalleryLink = true, enquiryProduct }) => {
  const src = image || HERO_IMAGES.wellsideHilux;
  return (
    <section data-testid="demo-banner" className="border-y border-[var(--border-default)] bg-[var(--bg-secondary)]">
      <div className="container-x py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">

          <div className="lg:col-span-4 relative aspect-[4/3] overflow-hidden border border-[var(--border-default)]">
            <img
              src={src}
              alt={`${product} demonstrator in Christchurch`}
              className="w-full h-full object-cover"
            />
            <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--brand-primary)] bg-black/70 backdrop-blur-sm px-3 py-1.5">
              CHRISTCHURCH · NZ
            </span>
          </div>

          <div className="lg:col-span-8">
            <p className="overline mb-4">— See It In Person</p>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white leading-[0.95]">
              VIEW OUR DEMONSTRATORS<br />
              <span className="text-[var(--brand-primary)]">IN CHRISTCHURCH.</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-5 max-w-xl leading-relaxed">
              Photos only tell half the story. Book a viewing to open the doors, feel the construction and check the finish for yourself. Demonstrators available by appointment for serious buyers, trade and fleet enquiries.
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={1.4} />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)]">Location</p>
                  <p className="text-sm text-white mt-1">Christchurch, NZ</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar size={18} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={1.4} />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)]">Availability</p>
                  <p className="text-sm text-white mt-1">By appointment</p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {enquiryProduct ? (
                <button type="button" onClick={() => goToEnquiryAs(enquiryProduct)} data-testid="demo-banner-book" className="btn-primary">
                  Book A Viewing <ArrowUpRight size={14} />
                </button>
              ) : (
                <a href="#enquire" data-testid="demo-banner-book" className="btn-primary">
                  Book A Viewing <ArrowUpRight size={14} />
                </a>
              )}
              {showGalleryLink && (
                <a href="https://photos.app.goo.gl/FAy62ZWh6aVKq34D7" target="_blank" rel="noopener noreferrer" data-testid="demo-banner-gallery" className="btn-ghost">
                  Or Browse The Gallery
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
